import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FundraisingFund, 
  FundraisingTermSheet,
  TERM_SHEET_STATUSES, 
  TermSheetStatus 
} from '../../types';
import TermSheetKanbanCard from './TermSheetKanbanCard';
import { EyeOff, Eye } from "lucide-react";

// Flattened term sheet with fund data
interface TermSheetWithFund extends FundraisingTermSheet {
  fund: FundraisingFund;
}

interface TermSheetKanbanBoardProps {
  funds: FundraisingFund[];
  onStatusChange: (termSheetId: string, fundId: string, newStatus: TermSheetStatus) => void;
}

// Filter out terminal statuses for main board
const ACTIVE_STATUSES = TERM_SHEET_STATUSES.filter(s => 
  !['declined', 'expired'].includes(s.status)
);

export default function TermSheetKanbanBoard({ funds, onStatusChange }: TermSheetKanbanBoardProps) {
  const navigate = useNavigate();
  const [draggedTermSheet, setDraggedTermSheet] = useState<TermSheetWithFund | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  // Flatten term sheets with fund data
  const allTermSheets: TermSheetWithFund[] = [];
  funds.forEach(fund => {
    (fund.termSheets || []).forEach(ts => {
      allTermSheets.push({ ...ts, fund });
    });
  });

  const activeTermSheets = allTermSheets.filter(ts => 
    !['declined', 'expired'].includes(ts.status)
  );
  const archivedTermSheets = allTermSheets.filter(ts => 
    ['declined', 'expired'].includes(ts.status)
  );

  const groupedTermSheets = ACTIVE_STATUSES.reduce((acc, { status }) => {
    acc[status] = activeTermSheets.filter(ts => ts.status === status);
    return acc;
  }, {} as Record<TermSheetStatus, TermSheetWithFund[]>);

  const handleDragStart = (e: React.DragEvent, termSheet: TermSheetWithFund) => {
    setDraggedTermSheet(termSheet);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, targetStatus: TermSheetStatus) => {
    e.preventDefault();
    if (draggedTermSheet && draggedTermSheet.status !== targetStatus) {
      onStatusChange(draggedTermSheet.id, draggedTermSheet.fund.id, targetStatus);
    }
    setDraggedTermSheet(null);
  };

  const handleDragEnd = () => {
    setDraggedTermSheet(null);
  };

  const getColumnTotal = (status: TermSheetStatus) => {
    const statusSheets = groupedTermSheets[status] || [];
    return statusSheets.reduce((sum, ts) => sum + ts.investmentAmount, 0);
  };

  const formatCurrency = (value: number) => {
    if (value >= 1000000000) return `$${(value / 1000000000).toFixed(1)}B`;
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
  };

  if (allTermSheets.length === 0) {
    return (
      <div className="rounded-xl border border-neutral-200 bg-neutral-50/50 p-8 text-center">
        <p className="text-neutral-500">No term sheets received yet</p>
        <p className="text-xs text-neutral-400 mt-1">Term sheets will appear here when funds send offers</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Archived Drop Zone */}
      <div
        className={`rounded-xl border transition-colors ${
          draggedTermSheet ? 'border-red-300 bg-red-50/50' : 'border-neutral-200 bg-neutral-50/50'
        }`}
        onDragOver={handleDragOver}
        onDrop={(e) => handleDrop(e, 'declined')}
      >
        <button
          onClick={() => setShowArchived(!showArchived)}
          className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-neutral-100/50 rounded-xl transition-colors"
        >
          <div className="flex items-center gap-3">
            <span className="text-[11px] uppercase tracking-wider text-neutral-500 font-medium">
              Declined / Expired
            </span>
            <span className="text-[11px] text-neutral-400">
              {archivedTermSheets.length} term sheets
            </span>
          </div>
          <div className="flex items-center gap-2 text-neutral-400">
            {showArchived ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            <span className="text-[10px]">{showArchived ? 'Hide' : 'Show'}</span>
          </div>
        </button>

        {showArchived && archivedTermSheets.length > 0 && (
          <div className="px-4 pb-4 border-t border-neutral-100">
            <div className="grid grid-cols-4 gap-2 mt-3">
              {archivedTermSheets.map(ts => (
                <TermSheetKanbanCard
                  key={ts.id}
                  termSheet={ts}
                  fund={ts.fund}
                  isDragging={draggedTermSheet?.id === ts.id}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  onClick={() => navigate(`/fundraising/funds/${ts.fund.id}`)}
                  compact
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Kanban Columns */}
      <div className="grid grid-cols-4 gap-3">
        {ACTIVE_STATUSES.map(({ status, label, color, bgColor }) => (
          <div
            key={status}
            className="flex flex-col min-h-[400px]"
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, status)}
          >
            {/* Column Header */}
            <div className={`rounded-t-xl px-3 py-2.5 border border-b-0 border-neutral-200 ${bgColor}`}>
              <div className="flex items-center justify-between">
                <span className={`text-[11px] uppercase tracking-wider font-medium ${color}`}>
                  {label}
                </span>
                <span className="text-[10px] text-neutral-400 bg-white/50 px-1.5 py-0.5 rounded">
                  {groupedTermSheets[status]?.length || 0}
                </span>
              </div>
              <div className="text-[10px] text-neutral-500 mt-0.5">
                {formatCurrency(getColumnTotal(status))}
              </div>
            </div>

            {/* Column Body */}
            <div className={`flex-1 bg-neutral-50/50 rounded-b-xl border border-neutral-200 p-2 space-y-2 ${
              draggedTermSheet && draggedTermSheet.status !== status ? 'ring-2 ring-blue-200' : ''
            }`}>
              {(!groupedTermSheets[status] || groupedTermSheets[status].length === 0) ? (
                <div className="h-14 flex items-center justify-center text-neutral-300 text-[11px] border border-dashed border-neutral-200 rounded-lg">
                  Drop here
                </div>
              ) : (
                groupedTermSheets[status].map(ts => (
                  <TermSheetKanbanCard
                    key={ts.id}
                    termSheet={ts}
                    fund={ts.fund}
                    isDragging={draggedTermSheet?.id === ts.id}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onClick={() => navigate(`/fundraising/funds/${ts.fund.id}`)}
                  />
                ))
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
